import { Injectable } from '@angular/core'
import { AudioRecordingService } from './audio-recording.service'

@Injectable({
  providedIn: 'root',
})
export class AudioAnalyzerService {
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private animationFrameId: number | null = null
  private isRendering = false
  private currentDialogState: string = 'IDLE'
  private previousDialogState: string = 'IDLE'

  // 発話時間管理
  private userSpeakingStartTime: number | null = null
  private listeningStartTime: number | null = null
  private processingStartTime: number | null = null
  private aiSpeakingStartTime: number | null = null

  // 弾幕（transcript流し）管理
  private danmakuItems: Array<{
    text: string
    x: number
    y: number
    speed: number
    color: string
    width: number
  }> = []

  // オーディオ設定
  public audioSettings = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  }

  constructor(private audioRecordingService: AudioRecordingService) {}

  /**
   * Canvas要素を設定して描画を初期化
   */
  setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')
  }

  /**
   * 現在のダイアログ状態を設定
   */
  setDialogState(state: string): void {
    const now = performance.now()

    // 状態更新前に現在の状態を保存
    const prevState = this.currentDialogState

    // LISTENING（会話受付）タイマー管理
    if (state === 'LISTENING' && prevState !== 'LISTENING') {
      // 会話受付開始
      this.listeningStartTime = now
    } else if (
      prevState === 'LISTENING' &&
      state !== 'LISTENING' &&
      state !== 'USER_SPEAKING'
    ) {
      // 会話受付終了（USER_SPEAKINGへの遷移は継続）
      this.listeningStartTime = null
    }

    // USER_SPEAKING（実際の発話）タイマー管理
    if (state === 'USER_SPEAKING' && prevState !== 'USER_SPEAKING') {
      // ユーザー発話開始
      this.userSpeakingStartTime = now
    } else if (prevState === 'USER_SPEAKING' && state !== 'USER_SPEAKING') {
      // ユーザー発話終了
      this.userSpeakingStartTime = null

      // LISTENING以外に遷移する場合はlisteningもリセット
      if (state !== 'LISTENING') {
        this.listeningStartTime = null
      }
    }

    // PROCESSING（AI処理中）タイマー管理
    if (state === 'PROCESSING' && prevState !== 'PROCESSING') {
      // 処理開始
      this.processingStartTime = now
    }

    // AI発話タイマー管理
    if (state === 'AI_SPEAKING' && prevState !== 'AI_SPEAKING') {
      // AI発話開始 → Processingタイマーを停止
      this.processingStartTime = null
      this.aiSpeakingStartTime = now
    } else if (prevState === 'AI_SPEAKING' && state !== 'AI_SPEAKING') {
      // AI発話終了
      this.aiSpeakingStartTime = null
    }

    this.previousDialogState = this.currentDialogState
    this.currentDialogState = state
  }

  /**
   * transcriptを弾幕として追加
   */
  addTranscript(text: string, type: 'user' | 'ai' | 'error' | 'interim'): void {
    if (!this.canvas || !this.ctx) {
      return
    }

    // テキストの幅を測定
    this.ctx.font = 'bold 16px sans-serif'
    const textWidth = this.ctx.measureText(text).width

    // 色を決定
    let color: string
    switch (type) {
      case 'user':
        color = '#00ff88' // 緑（確定発話）
        break
      case 'ai':
        color = '#ff88ff' // ピンク（AI応答）
        break
      case 'error':
        color = '#ff4444' // 赤（エラー）
        break
      case 'interim':
        color = '#ffff00' // 黄色（認識途中）
        break
    }

    // ランダムな縦位置（上部20px〜下部-20pxの範囲）
    const y = 20 + Math.random() * (this.canvas.height - 40)

    // 速度（ピクセル/フレーム）
    const speed = 2 + Math.random() * 2

    this.danmakuItems.push({
      text,
      x: this.canvas.width, // 右端から開始
      y,
      speed,
      color,
      width: textWidth,
    })
  }

  /**
   * 波形描画を開始
   */
  start(): void {
    if (this.isRendering) {
      return
    }
    this.isRendering = true
    this.render()
  }

  /**
   * 波形描画を停止
   */
  stop(): void {
    this.isRendering = false
    if (this.animationFrameId != null) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
  }

  /**
   * 波形を描画（1フレーム）
   */
  private render(): void {
    if (!this.isRendering || !this.canvas || !this.ctx) {
      return
    }

    // 周波数データ取得
    const frequencyData = this.audioRecordingService.getFrequencyData()
    if (!frequencyData) {
      // データがない場合も次フレームをスケジュール
      this.animationFrameId = requestAnimationFrame(() => this.render())
      return
    }

    const width = this.canvas.width
    const height = this.canvas.height

    // 背景クリア（半透明黒でトレイル効果）
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.2)'
    this.ctx.fillRect(0, 0, width, height)

    // デバッグ情報取得
    const audioInfo = this.audioRecordingService.getCurrentAudioInfo()
    const vadSettings = this.audioRecordingService.getVADSettings()
    // レベルバー描画（左端）
    this.renderLevelBar(audioInfo, vadSettings, height)

    // 周波数バー描画（レベルバーの右側から）
    this.renderFrequencyBars(frequencyData, height)

    // 発話時間計算
    const now = performance.now()
    const userSpeakingDuration = this.userSpeakingStartTime
      ? (now - this.userSpeakingStartTime) / 1000
      : 0
    const listeningDuration = this.listeningStartTime
      ? (now - this.listeningStartTime) / 1000
      : 0
    const processingDuration = this.processingStartTime
      ? (now - this.processingStartTime) / 1000
      : 0
    const aiSpeakingDuration = this.aiSpeakingStartTime
      ? (now - this.aiSpeakingStartTime) / 1000
      : 0

    // デバッグ情報表示
    this.renderDebugInfo(
      audioInfo,
      vadSettings,
      userSpeakingDuration,
      listeningDuration,
      processingDuration,
      aiSpeakingDuration,
    )

    // 弾幕描画とアニメーション
    this.renderDanmaku()

    // 次フレームスケジュール
    this.animationFrameId = requestAnimationFrame(() => this.render())
  }

  /**
   * レベルバーを描画
   */
  private renderLevelBar(
    audioInfo: { currentLevel: number; isDetectingVoice: boolean },
    vadSettings: { silenceThreshold: number },
    height: number,
  ): void {
    if (!this.ctx) return

    const levelBarWidth = 12
    const levelBarHeight = (audioInfo.currentLevel / 100) * height
    const levelBarX = 4
    const levelBarY = height - levelBarHeight

    // レベルバー本体
    this.ctx.fillStyle = '#ff8800' // オレンジ色
    this.ctx.fillRect(levelBarX, levelBarY, levelBarWidth, levelBarHeight)

    // レベルバーの枠線
    this.ctx.strokeStyle = '#ffffff'
    this.ctx.lineWidth = 1
    this.ctx.strokeRect(levelBarX, 0, levelBarWidth, height)

    // 閾値ライン（レベルバー上）
    const thresholdY = height - (vadSettings.silenceThreshold / 100) * height
    this.ctx.strokeStyle = '#ff0000' // 赤色で閾値ライン
    this.ctx.lineWidth = 1
    this.ctx.beginPath()
    this.ctx.moveTo(levelBarX, thresholdY)
    this.ctx.lineTo(levelBarX + levelBarWidth, thresholdY)
    this.ctx.stroke()
  }

  /**
   * 周波数バーを描画
   */
  private renderFrequencyBars(frequencyData: Uint8Array, height: number): void {
    if (!this.ctx || !this.canvas) return

    const levelBarWidth = 12
    const levelBarX = 4
    const frequencyStartX = levelBarX + levelBarWidth + 8 // レベルバーから8px離す
    const frequencyWidth = this.canvas.width - frequencyStartX
    const barWidth = frequencyWidth / frequencyData.length

    this.ctx.fillStyle = '#00ff88' // 緑色

    for (let i = 0; i < frequencyData.length; i++) {
      const barHeight = (frequencyData[i] / 255) * height
      const x = frequencyStartX + i * barWidth
      const y = height - barHeight

      this.ctx.fillRect(x, y, barWidth - 1, barHeight)
    }
  }

  /**
   * 弾幕を描画してアニメーション
   */
  private renderDanmaku(): void {
    if (!this.ctx) return

    this.ctx.font = 'bold 16px sans-serif'
    this.ctx.shadowBlur = 4
    this.ctx.shadowColor = 'rgba(0, 0, 0, 0.8)'

    // 弾幕を描画して移動
    for (let i = this.danmakuItems.length - 1; i >= 0; i--) {
      const item = this.danmakuItems[i]

      // 描画
      this.ctx.fillStyle = item.color
      this.ctx.fillText(item.text, item.x, item.y)

      // 位置を更新（右から左へ移動）
      item.x -= item.speed

      // 画面外に出たら削除
      if (item.x + item.width < 0) {
        this.danmakuItems.splice(i, 1)
      }
    }

    // シャドウをリセット
    this.ctx.shadowBlur = 0
  }

  /**
   * デバッグ情報を描画
   */
  private renderDebugInfo(
    audioInfo: {
      currentLevel: number
      isDetectingVoice: boolean
      confidence: number
    },
    vadSettings: { silenceThreshold: number },
    userSpeakingDuration: number,
    listeningDuration: number,
    processingDuration: number,
    aiSpeakingDuration: number,
  ): void {
    if (!this.ctx) return

    const levelBarWidth = 12
    const levelBarX = 4
    const debugTextX = levelBarX + levelBarWidth + 8

    this.ctx.fillStyle = '#ffffff'
    this.ctx.font = '11px monospace'
    this.ctx.fillText(
      `Level: ${audioInfo.currentLevel.toFixed(1)}`,
      debugTextX,
      16,
    )
    this.ctx.fillText(
      `Threshold: ${vadSettings.silenceThreshold}`,
      debugTextX,
      30,
    )
    this.ctx.fillText(`State: ${this.currentDialogState}`, debugTextX, 44)
    this.ctx.fillText(
      `Voice: ${audioInfo.isDetectingVoice ? 'YES' : 'NO'}`,
      debugTextX,
      58,
    )
    this.ctx.fillText(`Confidence: ${audioInfo.confidence}%`, debugTextX, 72)

    // 発話時間表示
    if (listeningDuration > 0) {
      this.ctx.fillStyle = '#ffff00' // 黄色で待機時間
      this.ctx.fillText(
        `Ready: ${listeningDuration.toFixed(1)}s`,
        debugTextX,
        86,
      )
    }
    if (userSpeakingDuration > 0) {
      this.ctx.fillStyle = '#00ff88' // 緑色でユーザー発話
      this.ctx.fillText(
        `User: ${userSpeakingDuration.toFixed(1)}s`,
        debugTextX,
        100,
      )
    }
    if (processingDuration > 0) {
      this.ctx.fillStyle = '#ffa500' // オレンジ色でAI処理中
      this.ctx.fillText(
        `Processing: ${processingDuration.toFixed(1)}s`,
        debugTextX,
        114,
      )
    }
    if (aiSpeakingDuration > 0) {
      this.ctx.fillStyle = '#ff88ff' // ピンク色でAI発話
      this.ctx.fillText(
        `AI: ${aiSpeakingDuration.toFixed(1)}s`,
        debugTextX,
        128,
      )
    }
  }

  /**
   * エコーキャンセレーションをトグル
   */
  async toggleEchoCancellation(): Promise<void> {
    this.audioSettings.echoCancellation = !this.audioSettings.echoCancellation
    await this.audioRecordingService.updateAudioSettings({
      echoCancellation: this.audioSettings.echoCancellation,
    })
    console.log(
      `Echo cancellation: ${this.audioSettings.echoCancellation ? 'ON' : 'OFF'}`,
    )
  }

  /**
   * ノイズサプレッションをトグル
   */
  async toggleNoiseSuppression(): Promise<void> {
    this.audioSettings.noiseSuppression = !this.audioSettings.noiseSuppression
    await this.audioRecordingService.updateAudioSettings({
      noiseSuppression: this.audioSettings.noiseSuppression,
    })
    console.log(
      `Noise suppression: ${this.audioSettings.noiseSuppression ? 'ON' : 'OFF'}`,
    )
  }

  /**
   * オートゲインコントロールをトグル
   */
  async toggleAutoGainControl(): Promise<void> {
    this.audioSettings.autoGainControl = !this.audioSettings.autoGainControl
    await this.audioRecordingService.updateAudioSettings({
      autoGainControl: this.audioSettings.autoGainControl,
    })
    console.log(
      `Auto gain control: ${this.audioSettings.autoGainControl ? 'ON' : 'OFF'}`,
    )
  }

  /**
   * クリーンアップ
   */
  cleanup(): void {
    this.stop()
    this.canvas = null
    this.ctx = null
    this.userSpeakingStartTime = null
    this.listeningStartTime = null
    this.processingStartTime = null
    this.aiSpeakingStartTime = null
    this.currentDialogState = 'IDLE'
    this.previousDialogState = 'IDLE'
    this.danmakuItems = []
  }
}
